"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, MoreHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@erp/shared-libs/hooks";
import { cn } from "@erp/shared-libs/utils";
import { BatchActionMenu } from "../menus/batch-action-menu";
import { Button } from "../ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "../ui/drawer";
import {
  OptimizedTooltip,
  OptimizedTooltipContent,
  OptimizedTooltipTrigger,
} from "../ui/optimized-tooltip";
import { Separator } from "../ui/separator";
import {
  countFittingItems,
  flattenPageActions,
  groupPageActions,
  PageAction,
  resolveMobilePageActions,
  splitInlineCandidates,
} from "./page-actions-util";

/**
 * 手機選取列上按鈕之間的間距（px，對應 gap-1.5）
 */
const MOBILE_BAR_GAP = 6;

/**
 * 加上「能不能用」之後的動作：元件統一判斷（要勾選的動作在沒有勾選時不能用），頁面給的停用條件也併進來
 */
type ResolvedPageAction = PageAction & {
  isDisabled: boolean;
  reason: string | undefined;
};

interface PageActionsProps {
  /**
   * 這一頁的動作。
   * 桌面照陣列順序排成按鈕；手機上陣列順序是優先順序（選取列放不下時，後面的先收進「更多」）。
   * 不該出現的動作（沒有權限、這個情境不適用）不要放進來
   */
  actions: PageAction[];
  /**
   * 目前勾選了幾筆
   */
  selectedCount: number;
  /**
   * 沒有勾選時，要勾選的動作不能用的原因（例：「請至少選擇一個商品」）；沒有傳就用通用的說法
   */
  emptySelectionReason?: string;
  /**
   * 取消全部勾選（手機選取列左邊的 ✕）
   */
  onClearSelection: () => void;
  /**
   * 樣式（套在標題列上那一組按鈕的外層）
   */
  className?: string;
}

/**
 * 列表頁上方的動作列。頁面只宣告有哪些動作、各是哪一種，怎麼排由這個元件決定。
 *
 * 桌面：照宣告的順序排成一排按鈕，放不下就換行；有子項目的動作是一顆下拉按鈕。
 *
 * 手機：
 * - 標題列：只放有文字的按鈕。有主要動作時，不用勾選的動作全部收進「⋯」；
 *   沒有主要動作而且只有一個時，那一個帶文字放在標題列
 * - 選取列：有勾選才從底部浮出，顯示已選幾筆；動作照順序能放幾個放幾個，放不下的收進「更多」。
 *   有子項目的動作攤平成一項一項；危險的動作（`isDestructive`）一律在「更多」的最下面
 *
 * 兩邊共通：要勾選的動作在沒有勾選時不能用
 */
export function PageActions({
  actions,
  selectedCount,
  emptySelectionReason,
  onClearSelection,
  className,
}: PageActionsProps) {
  // =====================================================
  // Hooks
  // =====================================================
  const { t } = useTranslation("common");

  /**
   * 是否為手機版
   */
  const isMobile = useIsMobile();

  // =====================================================
  // Functions
  // =====================================================
  /**
   * 替一個動作（與它底下的項目）加上能不能用與原因：
   * 要勾選的動作在沒有勾選時不能用，其餘照頁面給的條件
   */
  const resolveAction = (action: PageAction): ResolvedPageAction => {
    const isSelectionMissing = action.kind === "selection" && selectedCount === 0;

    return {
      ...action,
      isDisabled: isSelectionMissing || action.disabled === true,
      reason: isSelectionMissing
        ? (emptySelectionReason ?? t("page_actions.select_first"))
        : action.disabledReason,
    };
  };

  // =====================================================
  // Render
  // =====================================================
  return isMobile ? (
    <PageActionsMobile
      className={className}
      actions={flattenPageActions(actions).map(resolveAction)}
      selectedCount={selectedCount}
      onClearSelection={onClearSelection}
    />
  ) : (
    <div className={cn("flex flex-wrap items-center gap-4", className)}>
      {actions.map((action) =>
        action.items == null ? (
          <DesktopActionButton key={action.key} action={resolveAction(action)} />
        ) : (
          // 有子項目的動作：一顆下拉按鈕，每一項各自判斷能不能用（沒有項目就整顆不出現，避免開出空白選單）
          action.items.length > 0 && (
            <BatchActionMenu
              key={action.key}
              items={action.items.map((item) => {
                const resolvedItem = resolveAction({ ...item, kind: action.kind });

                return {
                  label: resolvedItem.label,
                  disabled: resolvedItem.isDisabled,
                  disabledTooltip:
                    resolvedItem.reason != null ? <p>{resolvedItem.reason}</p> : undefined,
                  onClick: item.onClick,
                };
              })}
            >
              <div className="flex items-center justify-center">
                {action.icon}
                <p className={cn("text-nowrap", action.icon != null && "ml-3")}>{action.label}</p>
                <ChevronDown className="ml-3" />
              </div>
            </BatchActionMenu>
          )
        )
      )}
    </div>
  );
}

/**
 * 桌面版的一顆動作按鈕；不能用時顯示成停用，滑過會說明原因
 */
const DesktopActionButton = ({ action }: { action: ResolvedPageAction }) => (
  <OptimizedTooltip hidden={!action.isDisabled || action.reason == null}>
    <OptimizedTooltipTrigger>
      <Button
        variant="dropdownPrimary"
        size="dropdownPrimary"
        disabled={action.isDisabled}
        onClick={action.onClick}
      >
        <div className="flex items-center justify-center">
          {action.icon}
          <p className={cn("text-nowrap", action.icon != null && "ml-3")}>{action.label}</p>
        </div>
      </Button>
    </OptimizedTooltipTrigger>
    <OptimizedTooltipContent>
      <p>{action.reason}</p>
    </OptimizedTooltipContent>
  </OptimizedTooltip>
);

/**
 * 量出一列放得下幾個動作。
 *
 * 做法：另外畫一份看不到的按鈕（全部的候選動作加上「更多」）量寬度，
 * 和這一列可用的寬度比；寬度或動作有變就重算
 *
 * @param gap 按鈕之間的間距（px）
 * @param hasForcedOverflow 有沒有一定要收進「更多」的動作
 * @param measureKey 候選動作的內容有變時要換的鍵（例：把名稱串起來）
 */
function useFittingCount({
  gap,
  hasForcedOverflow,
  measureKey,
}: {
  gap: number;
  hasForcedOverflow: boolean;
  measureKey: string;
}) {
  /**
   * 這一列可以用的範圍
   */
  const zoneRef = useRef<HTMLDivElement>(null);

  /**
   * 量寬度用、看不到的那一份按鈕（最後一個是「更多」）
   */
  const measureRef = useRef<HTMLDivElement>(null);

  /**
   * 放得下幾個；量到之前先當成全部放得下
   */
  const [fittingCount, setFittingCount] = useState(Number.POSITIVE_INFINITY);

  /**
   * 這一列的寬度或按鈕的寬度有變就重算。
   * ResizeObserver 在開始觀察時就會回報一次，而且是在畫面繪製之前，所以不會先閃一下全部再收起來
   */
  useEffect(() => {
    const zone = zoneRef.current;
    const measure = measureRef.current;

    if (zone == null || measure == null) return;

    const observer = new ResizeObserver(() => {
      const widths = Array.from(measure.children, (child) => (child as HTMLElement).offsetWidth);

      setFittingCount(
        countFittingItems({
          itemWidths: widths.slice(0, -1),
          gap,
          availableWidth: zone.clientWidth,
          moreWidth: widths[widths.length - 1] ?? 0,
          hasForcedOverflow,
        })
      );
    });

    observer.observe(zone);
    observer.observe(measure);

    return () => observer.disconnect();
  }, [gap, hasForcedOverflow, measureKey]);

  return { zoneRef, measureRef, fittingCount };
}

interface PageActionsMobileProps {
  className?: string;
  /**
   * 這一頁的動作（有子項目的已經攤平、也已經算好能不能用）
   */
  actions: ResolvedPageAction[];
  selectedCount: number;
  onClearSelection: () => void;
}

/**
 * 手機版：標題列上的按鈕、勾選後才出現的底部選取列，以及收起來的動作清單（底部抽屜）
 */
function PageActionsMobile({
  className,
  actions,
  selectedCount,
  onClearSelection,
}: PageActionsMobileProps) {
  // =====================================================
  // Hooks
  // =====================================================
  const { t } = useTranslation("common");

  /**
   * 動作清單（底部抽屜）是否開啟
   */
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  /**
   * 動作清單要列哪一組：不用勾選的動作，或選取列放不下的動作。
   * 關閉時保留上一次的值，抽屜滑下去的過程中內容才不會先消失
   */
  const [sheetKind, setSheetKind] = useState<"page" | "selection">("page");

  // =====================================================
  // Variables
  // =====================================================
  /**
   * 依種類分開的動作
   */
  const { primaryAction, pageActions, selectionActions } = groupPageActions(actions);

  /**
   * 標題列上不用勾選的動作怎麼放（只有文字的按鈕才放標題列，其餘收進「⋯」）
   */
  const { inlineAction, overflowActions: pageOverflowActions } = resolveMobilePageActions(
    pageActions,
    primaryAction != null
  );

  /**
   * 要勾選的動作裡，可以直接放在選取列上的與一定收進「更多」的（危險的動作）
   */
  const { inlineCandidates, forcedOverflowActions } = splitInlineCandidates(selectionActions);

  /**
   * 選取列要不要出現：有勾選、而且這一頁有要勾選的動作
   */
  const isSelectionBarVisible = selectedCount > 0 && selectionActions.length > 0;

  /**
   * 量選取列放得下幾個動作
   */
  const { zoneRef, measureRef, fittingCount } = useFittingCount({
    gap: MOBILE_BAR_GAP,
    hasForcedOverflow: forcedOverflowActions.length > 0,
    // 選取列出現與否也要重量（它不出現時量不到寬度）
    measureKey: `${isSelectionBarVisible}|${inlineCandidates.map((action) => action.label).join("|")}`,
  });

  /**
   * 直接放在選取列上的動作
   */
  const barActions = inlineCandidates.slice(0, fittingCount);

  /**
   * 選取列放不下、收進「更多」的動作（不含危險的動作）
   */
  const barOverflowActions = inlineCandidates.slice(barActions.length);

  /**
   * 動作清單這一次要列的動作：一般的在上面，危險的在最下面
   */
  const sheetActions =
    sheetKind === "page"
      ? { normal: pageOverflowActions, destructive: [] as ResolvedPageAction[] }
      : { normal: barOverflowActions, destructive: forcedOverflowActions };

  // =====================================================
  // Functions
  // =====================================================
  /**
   * 點列上的動作：不能用時不執行，改成跳出原因（手機沒有滑過提示，停用的按鈕點了要有回應）
   */
  const handleAction = (action: ResolvedPageAction) => {
    if (action.isDisabled) {
      if (action.reason != null) {
        toast.warning(action.reason);
      }

      return;
    }

    action.onClick?.();
  };

  /**
   * 打開動作清單
   */
  const handleOpenSheet = (kind: "page" | "selection") => {
    setSheetKind(kind);
    setIsSheetOpen(true);
  };

  // =====================================================
  // Render
  // =====================================================
  return (
    <>
      {/* 標題列上的按鈕：樣式和桌面版的按鈕相同 */}
      <div className={cn("flex shrink-0 items-center gap-2", className)}>
        {/* 只有文字的按鈕才放標題列；圖示鈕看不出是什麼動作，所以其餘一律收進「⋯」 */}
        {inlineAction != null ? (
          <MobileHeaderButton action={inlineAction} onClick={() => handleAction(inlineAction)} />
        ) : null}

        {/* 主要動作和「⋯」併成一顆：左邊是主要動作、右邊是「⋯」，兩段共用中間那條邊線 */}
        <div className="flex shrink-0 items-center">
          {primaryAction != null ? (
            <MobileHeaderButton
              action={primaryAction}
              className={cn(pageOverflowActions.length > 0 && "rounded-r-none")}
              onClick={() => handleAction(primaryAction)}
            />
          ) : null}

          {pageOverflowActions.length > 0 ? (
            <Button
              variant="dropdownPrimary"
              size="dropdownPrimary"
              className={cn(
                "shrink-0 [&_svg]:size-4",
                // 併在主要動作右邊時和排序鈕同一種接法：左邊不畫邊線，只留主要動作那一條當分隔
                primaryAction != null ? "rounded-l-none border-l-0 pr-3.5 pl-3" : "size-9 px-0"
              )}
              aria-label={t("page_actions.more_actions")}
              onClick={() => handleOpenSheet("page")}
            >
              <MoreHorizontal />
            </Button>
          ) : null}
        </div>
      </div>

      {/* 選取列：固定在畫面底部，掛到 body 才不會被頁面裡的捲動容器或 transform 影響定位 */}
      {isSelectionBarVisible
        ? createPortal(
            <div
              className="animate-in fade-in slide-in-from-bottom-3 fixed inset-x-3
                bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-40 duration-200"
            >
              <div
                className="bg-foreground dark:bg-primary text-primary-foreground flex items-center gap-1.5 rounded-2xl p-1.5
                  shadow-xl"
              >
                <Button
                  variant="ghostInverse"
                  className="size-9 shrink-0 rounded-xl p-0 [&_svg]:size-4"
                  aria-label={t("page_actions.clear_selection")}
                  onClick={onClearSelection}
                >
                  <X />
                </Button>

                <span className="shrink-0 text-nowrap text-sm font-bold tabular-nums">
                  {t("page_actions.selected_count", { count: selectedCount })}
                </span>

                <div
                  ref={zoneRef}
                  className="relative flex min-w-0 flex-1 items-center justify-end gap-1.5"
                >
                  {barActions.map((action) => (
                    <Button
                      key={action.key}
                      variant="ghostInverse"
                      className={cn(
                        "bg-primary-foreground/15 h-9 shrink-0 rounded-xl px-3",
                        action.isDisabled && "opacity-50"
                      )}
                      aria-disabled={action.isDisabled}
                      onClick={() => handleAction(action)}
                    >
                      {action.label}
                    </Button>
                  ))}

                  {barOverflowActions.length + forcedOverflowActions.length > 0 ? (
                    <Button
                      variant="ghostInverse"
                      className="bg-primary-foreground/15 h-9 shrink-0 rounded-xl px-3"
                      onClick={() => handleOpenSheet("selection")}
                    >
                      {t("page_actions.more")}
                    </Button>
                  ) : null}

                  {/* 量寬度用的那一份：看不到、不佔位置 */}
                  <div
                    aria-hidden
                    className="pointer-events-none invisible absolute size-0 overflow-hidden"
                  >
                    <div ref={measureRef} className="flex w-max gap-1.5">
                      {inlineCandidates.map((action) => (
                        <Button
                          key={action.key}
                          variant="ghostInverse"
                          className="h-9 rounded-xl px-3"
                          tabIndex={-1}
                        >
                          {action.label}
                        </Button>
                      ))}
                      <Button variant="ghostInverse" className="h-9 rounded-xl px-3" tabIndex={-1}>
                        {t("page_actions.more")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {/* 收起來的動作清單 */}
      <Drawer open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <DrawerContent className="max-h-[78vh] rounded-t-2xl pb-[env(safe-area-inset-bottom)]">
          {/* DrawerContent 是橫向的 flex 容器，內層要 flex-1 才會撐滿寬度 */}
          <div className="flex max-h-[78vh] min-h-0 flex-1 flex-col overflow-hidden">
            <div className="bg-border mx-auto mt-2 h-1 w-10 shrink-0 rounded-full" />

            <DrawerTitle className="shrink-0 px-5 pb-2 pt-3 text-base font-bold">
              {sheetKind === "page"
                ? t("page_actions.more_actions")
                : t("page_actions.selected_count", { count: selectedCount })}
            </DrawerTitle>
            <DrawerDescription className="hidden" />

            <div className="flex min-h-0 flex-col overflow-y-auto px-2 pb-3 [&>*]:shrink-0">
              {sheetActions.normal.map((action) => (
                <MobileSheetItem
                  key={action.key}
                  action={action}
                  onSelect={() => {
                    setIsSheetOpen(false);
                    action.onClick?.();
                  }}
                />
              ))}

              {/* 危險的動作和其他動作隔開，放最下面 */}
              {sheetActions.normal.length > 0 && sheetActions.destructive.length > 0 ? (
                <Separator className="mx-3 my-1.5 w-auto" />
              ) : null}

              {sheetActions.destructive.map((action) => (
                <MobileSheetItem
                  key={action.key}
                  action={action}
                  onSelect={() => {
                    setIsSheetOpen(false);
                    action.onClick?.();
                  }}
                />
              ))}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/**
 * 手機標題列上有文字的按鈕；樣式和桌面版的按鈕相同
 */
const MobileHeaderButton = ({
  action,
  className,
  onClick,
}: {
  action: ResolvedPageAction;
  className?: string;
  onClick: () => void;
}) => (
  <Button
    variant="dropdownPrimary"
    size="dropdownPrimary"
    className={cn("shrink-0", className)}
    aria-disabled={action.isDisabled}
    onClick={onClick}
  >
    <div className="flex items-center justify-center">
      {action.icon}
      <p className={cn("text-nowrap", action.icon != null && "ml-3")}>{action.label}</p>
    </div>
  </Button>
);

/**
 * 手機動作清單裡的一項；不能用時在右邊寫出原因
 */
const MobileSheetItem = ({
  action,
  onSelect,
}: {
  action: ResolvedPageAction;
  onSelect: () => void;
}) => (
  <Button
    variant="ghost"
    className={cn(
      "[&_svg]:size-4.5 h-12 w-full justify-start gap-3 rounded-xl px-3 text-[15px] font-normal",
      action.isDestructive && "text-destructive"
    )}
    disabled={action.isDisabled}
    onClick={onSelect}
  >
    {action.icon}
    <span className="text-nowrap">{action.label}</span>
    {action.isDisabled && action.reason != null ? (
      <span className="text-muted-foreground-darker ml-auto truncate pl-3 text-xs">
        {action.reason}
      </span>
    ) : null}
  </Button>
);
